/**
 * Edit Share Book Modal
 *
 * Modal for editing share book settings including clauses.
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import { useUpdateShareBook, useDeleteShareBook } from "@/hooks/use-share-book";
import { Info, AlertTriangle, Trash2 } from "lucide-react";

interface EditShareBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  shareBook: {
    shareCapital: string;
    hasPreemptionClause: boolean;
    hasConsentClause: boolean;
    hasRedemptionClause: boolean;
    hasConversionClause: boolean;
  };
}

const shareBookSchema = z.object({
  shareCapital: z.number().min(25000, "Aktiekapitalet måste vara minst 25 000 kr"),
  hasPreemptionClause: z.boolean(),
  hasConsentClause: z.boolean(),
  hasRedemptionClause: z.boolean(),
  hasConversionClause: z.boolean(),
});

type ShareBookFormData = z.infer<typeof shareBookSchema>;

export function EditShareBookModal({
  isOpen,
  onClose,
  companyId,
  shareBook,
}: EditShareBookModalProps) {
  const navigate = useNavigate();
  const updateShareBook = useUpdateShareBook();
  const deleteShareBook = useDeleteShareBook();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ShareBookFormData>({
    resolver: zodResolver(shareBookSchema),
    defaultValues: {
      shareCapital: parseFloat(shareBook.shareCapital),
      hasPreemptionClause: shareBook.hasPreemptionClause,
      hasConsentClause: shareBook.hasConsentClause,
      hasRedemptionClause: shareBook.hasRedemptionClause,
      hasConversionClause: shareBook.hasConversionClause,
    },
  });

  // Reset form when shareBook changes
  useEffect(() => {
    reset({
      shareCapital: parseFloat(shareBook.shareCapital),
      hasPreemptionClause: shareBook.hasPreemptionClause,
      hasConsentClause: shareBook.hasConsentClause,
      hasRedemptionClause: shareBook.hasRedemptionClause,
      hasConversionClause: shareBook.hasConversionClause,
    });
  }, [shareBook, reset]);

  const onSubmit = async (data: ShareBookFormData) => {
    try {
      await updateShareBook.mutateAsync({
        companyId,
        data: {
          shareCapital: data.shareCapital,
          hasPreemptionClause: data.hasPreemptionClause,
          hasConsentClause: data.hasConsentClause,
          hasRedemptionClause: data.hasRedemptionClause,
          hasConversionClause: data.hasConversionClause,
        },
      });
      toast.success("Aktieboken har uppdaterats");
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte uppdatera aktieboken";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setShowDeleteConfirm(false);
    setDeleteConfirmText("");
    onClose();
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== "RADERA") {
      toast.error("Skriv RADERA för att bekräfta");
      return;
    }

    try {
      // force=true since user has confirmed by typing "RADERA"
      await deleteShareBook.mutateAsync({ companyId, force: true });
      toast.success("Aktieboken har raderats");
      handleClose();
      navigate(`/dashboard/companies/${companyId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Kunde inte radera aktiebok");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Redigera aktieboken" size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Share Capital */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Aktiekapital (SEK) *
          </label>
          <input
            type="number"
            {...register("shareCapital", { valueAsNumber: true })}
            className="input"
            min={25000}
            step={1}
          />
          {errors.shareCapital && (
            <p className="text-sm text-red-600 mt-1">{errors.shareCapital.message}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Minsta aktiekapital för aktiebolag är 25 000 kr enligt ABL.
          </p>
        </div>

        {/* Clauses */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-medium text-gray-900 mb-1">
            Förbehåll i bolagsordningen
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Välj vilka överlåtelsebegränsningar som finns enligt bolagsordningen.
          </p>

          <div className="space-y-4">
            {/* Förköpsförbehåll */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register("hasPreemptionClause")}
                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Förköpsförbehåll
                </span>
                <p className="text-xs text-gray-500">
                  Befintliga aktieägare har rätt att köpa aktier som ska överlåtas
                  innan de erbjuds till utomstående.
                </p>
              </div>
            </label>

            {/* Samtyckesförbehåll */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register("hasConsentClause")}
                className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Samtyckesförbehåll
                </span>
                <p className="text-xs text-gray-500">
                  Överlåtelse av aktier kräver styrelsens godkännande.
                </p>
              </div>
            </label>

            {/* Hembudsförbehåll */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register("hasRedemptionClause")}
                className="mt-1 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Hembudsförbehåll
                </span>
                <p className="text-xs text-gray-500">
                  Redan överlåtna aktier kan hembjudas till befintliga ägare
                  (lösenrätt efter överlåtelse).
                </p>
              </div>
            </label>

            {/* Omvandlingsförbehåll */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register("hasConversionClause")}
                className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Omvandlingsförbehåll
                </span>
                <p className="text-xs text-gray-500">
                  Aktier av ett slag kan omvandlas till ett annat under vissa villkor.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Info */}
        <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
          <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-800">
            Förbehållen ska vara registrerade i bolagsordningen hos Bolagsverket.
            Ändringar i förbehåll kräver ändring av bolagsordningen på bolagsstämma.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={handleClose}>
            Avbryt
          </Button>
          <Button type="submit" disabled={isSubmitting || !isDirty}>
            {isSubmitting ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Sparar...
              </>
            ) : (
              "Spara ändringar"
            )}
          </Button>
        </div>
      </form>

      {/* Delete Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-2">
          <AlertTriangle size={16} />
          Farlig zon
        </h3>

        {!showDeleteConfirm ? (
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
            <div>
              <p className="text-sm font-medium text-gray-900">Radera aktiebok</p>
              <p className="text-xs text-gray-500">
                Ta bort aktieboken och all relaterad data permanent.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-100"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 size={16} className="mr-2" />
              Radera
            </Button>
          </div>
        ) : (
          <div className="p-4 bg-red-50 rounded-lg border border-red-300 space-y-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Är du säker på att du vill radera aktieboken?
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Denna åtgärd kan inte ångras. All data raderas permanent:
                </p>
                <ul className="text-xs text-red-700 mt-2 list-disc list-inside space-y-1">
                  <li>Alla aktieslag</li>
                  <li>Alla aktieägare</li>
                  <li>Alla transaktioner</li>
                  <li>Hela transaktionshistoriken</li>
                </ul>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-red-800 mb-1">
                Skriv RADERA för att bekräfta
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-red-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                placeholder="RADERA"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText("");
                }}
              >
                Avbryt
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={deleteConfirmText !== "RADERA" || deleteShareBook.isPending}
                onClick={handleDelete}
              >
                {deleteShareBook.isPending ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    Raderar...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} className="mr-2" />
                    Radera aktieboken permanent
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
